import { Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import React, { Suspense, useEffect } from "react";
import PrivateRoute from "./components/PrivateRoute";
import { silentRefresh } from "./services/api";
import "primeicons/primeicons.css";
import "react-confirm-alert/src/react-confirm-alert.css";
import "react-toastify/dist/ReactToastify.css";
 
// Lazy load components
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const UserDashboard = React.lazy(() => import("./pages/UserDashboard"));
const GFI = React.lazy(() => import("./pages/gfi"));
const Login = React.lazy(() => import("./pages/Login"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const ResetPasswordConfirmation = React.lazy(() => import("./pages/ResetPasswordConfirmation"));
const ResetPasswordWithToken = React.lazy(() => import("./pages/ResetPasswordWithToken"));
const OnboardingData = React.lazy(() => import("./pages/OnboardingData"));
const FleetEdit = React.lazy(() => import("./components/FleetEdit"));
const VesselDetails = React.lazy(() => import("./pages/VesselDetails"));
const AccessDenied = React.lazy(() => import("./components/AccessDenied"));
const Reports = React.lazy(() => import("./pages/Reports"));
const DailyReport = React.lazy(() => import("./pages/DailyReports"));
const GeneralData = React.lazy(() => import("./pages/ReportingPlatform"));
const VesselDailyReport = React.lazy(() => import("./pages/VesselDailyReport"));
const LinkVessel = React.lazy(() => import("./pages/LinkVessel"));
const VesselDashboard = React.lazy(() => import("./pages/VesselDashboard"));
const ReportDashboard = React.lazy(() => import("./pages/ReportDashboard"));
const VesselReportsList = React.lazy(() => import("./pages/VesselReportsList"));
const TriggerDataPull = React.lazy(() => import("./pages/TriggerDataPull"));


// Simplified ModuleAccessCheck - now handles role-based access more cleanly
const ModuleAccessCheck = ({ children, moduleName, allowedRoles }) => {
  const userRole = localStorage.getItem("user_role");
  const gfiAccess = localStorage.getItem("gfi_access") === "true";
  const offshoreAccess = localStorage.getItem("offshore_access") === "true";
  const vesselReportAccess = localStorage.getItem("vessel_report_access") === "true"; // ADD THIS LINE
  const reportsAccess = localStorage.getItem("report_access") === "true"; // Correct key
 
  // Check if user role is allowed for this module
  const hasRoleAccess = allowedRoles.includes(userRole);
 
  if (!hasRoleAccess) {
    return (
      <AccessDenied
        moduleName={moduleName}
        message={`Only ${allowedRoles.join(", ")} roles have access to ${moduleName}.`}
        redirectPath="/login"
      />
    );
  }
 
  // Additional access flag checks for fleet/user/multiVessel roles
  if (
    userRole === "fleet" ||
    userRole === "user" ||
    userRole === "multiVessel"
  ) {
    if (moduleName === "Offshore" && !offshoreAccess) {
      return (
        <AccessDenied
          moduleName={moduleName}
          message="You don't have Offshore module access."
          redirectPath={gfiAccess ? "/gfi" : reportsAccess ? "/reports" : "/login"}
        />
      );
    }
    if (moduleName === "GFI" && !gfiAccess) {
      return (
        <AccessDenied
          moduleName={moduleName}
          message="You don't have GFI module access."
          redirectPath={offshoreAccess ? "/userdashboard" : reportsAccess ? "/reports" : "/login"}
        />
      );
    }

    // NEW: Reports module access check
    if (moduleName === "Reports" && !reportsAccess) {
      return (
        <AccessDenied
          moduleName={moduleName}
          message="You don't have Reports module access."
          redirectPath={offshoreAccess ? "/userdashboard" : gfiAccess ? "/gfi" : "/login"}
        />
      );
    }

    // Vessel Daily Report & Report Dashboard
    if ((moduleName === "VesselDailyReport" || moduleName === "ReportDashboard") && !vesselReportAccess) {
      return (
        <AccessDenied
          moduleName={moduleName}
          message="You don't have Vessel Report module access."
          redirectPath="/login"
        />
      );
    }

    // Vessel Report pages (list, dashboard, general data)
    if (moduleName === "VesselReport" && !vesselReportAccess && userRole !== "vessel") {
      return (
        <AccessDenied
          moduleName="Vessel Report"
          message="You don't have Vessel Report module access."
          redirectPath="/login"
        />
      );
    }
  }
 
  // Vessel users only need role check for DailyReports
  if (userRole === "vessel" && moduleName === "DailyReports") {
    return children;
  }
 
  return children;
};
 
// AccessDenied wrapper to handle query parameters
const AccessDeniedWrapper = () => {
  const [searchParams] = useSearchParams();
  const moduleName = searchParams.get("module") || "Module";
 
  return <AccessDenied moduleName={moduleName} />;
};
 
function App() {
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("access_token");
      const refreshToken = localStorage.getItem("refresh_token");
 
      if (refreshToken && !token) {
        await silentRefresh();
      }
    };
    checkAuth();
  }, []);
 
  useEffect(() => {
    const syncScroll = () => {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      const content = document.querySelector(".dashboard-content");
      if (content) {
        content.style.overflowY = "auto";
        content.scrollTop = 0;
        // Force bar show if content > viewport
        if (content.scrollHeight > content.clientHeight) {
          content.style.overflowY = "scroll"; // Visible bar
        }
      }
      window.dispatchEvent(new Event("resize"));
    };
    syncScroll();
    setTimeout(syncScroll, 300);
  }, []);
 
  return (
    <Suspense
      fallback={
       <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
      <div className="relative w-20 h-20">
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
          <span
            key={i}
            className="absolute top-1/2 left-1/2 w-4 h-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0d5a47] animate-pop-fade"
            style={{
              "--angle": `${angle}deg`,
              animationDelay: `${i * 200 - 1600}ms`,
            }}
          />
        ))}
      </div>
        </div>
      }
    >
   <Routes>
  {/* Public Routes */}
  <Route path="/login" element={<Login />} />
  <Route path="/reset-password" element={<ResetPassword />} />
  <Route path="/reset-password-confirmation" element={<ResetPasswordConfirmation />} />
  <Route path="/reset-password/:token" element={<ResetPasswordWithToken />} />
  {/* Also support query parameter format */}
  <Route path="/reset-password/confirm" element={<ResetPasswordWithToken />} />
        {/* Admin Routes */}
        <Route
    path="/admin"
    element={
      <PrivateRoute allowedRoles={["admin"]}>
        <AdminDashboard />
      </PrivateRoute>
    }
  />
        <Route
          path="/onboarding_data"
          element={
            <PrivateRoute allowedRoles={["admin"]}>
              <OnboardingData />
            </PrivateRoute>
          }
        />
        <Route
          path="/edit-fleet/:fleetId"
          element={
            <PrivateRoute allowedRoles={["admin"]}>
              <FleetEdit />
            </PrivateRoute>
          }
        />
        <Route
          path="/link-vessel"
          element={
            <PrivateRoute allowedRoles={["admin"]}>
              <LinkVessel />
            </PrivateRoute>
          }
        />
<Route
  path="/trigger-data-pull"
  element={
    <PrivateRoute allowedRoles={["admin"]}>
      <TriggerDataPull />
    </PrivateRoute>
  }
/>
 
        {/* Fleet/User/MultiVessel Routes */}
        <Route
          path="/userdashboard"
          element={
            <PrivateRoute allowedRoles={["fleet", "user", "multiVessel"]}>
              <ModuleAccessCheck moduleName="Offshore" allowedRoles={["fleet", "user", "multiVessel"]}>
                <UserDashboard />
              </ModuleAccessCheck>
            </PrivateRoute>
          }
        />
        <Route
          path="/gfi"
          element={
            <PrivateRoute
              allowedRoles={["fleet", "user", "multiVessel", "gfi"]}
            >
              <ModuleAccessCheck
                moduleName="GFI"
                allowedRoles={["fleet", "user", "multiVessel", "gfi"]}
              >
                <GFI />
              </ModuleAccessCheck>
            </PrivateRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <PrivateRoute allowedRoles={["fleet", "user", "multiVessel"]}>
              <ModuleAccessCheck moduleName="Reports" allowedRoles={["fleet", "user", "multiVessel"]}>
                <Reports />
              </ModuleAccessCheck>
            </PrivateRoute>
          }
        />
        <Route
          path="/vessel-daily-report"
          element={
            <PrivateRoute allowedRoles={["fleet", "multiVessel"]}>
              <ModuleAccessCheck
                moduleName="VesselDailyReport"
                allowedRoles={["fleet", "multiVessel"]}
              >
                <VesselDailyReport />
              </ModuleAccessCheck>
            </PrivateRoute>
          }
        />
        <Route
          path="/report-dashboard"
          element={
            <PrivateRoute allowedRoles={["fleet", "multiVessel"]}>
              <ModuleAccessCheck
                moduleName="ReportDashboard"
                allowedRoles={["fleet", "multiVessel"]}
              >
                <ReportDashboard />
              </ModuleAccessCheck>
            </PrivateRoute>
          }
        />

        {/* Vessel-related */}
        <Route
          path="/vessel-reports-list"
          element={
            <PrivateRoute allowedRoles={["vessel", "fleet", "multiVessel"]}>
              <ModuleAccessCheck moduleName="VesselReport" allowedRoles={["vessel", "fleet", "multiVessel"]}>
                <VesselReportsList />
              </ModuleAccessCheck>
            </PrivateRoute>
          }
        />

        <Route
          path="/general-data"
          element={
            <PrivateRoute allowedRoles={["fleet", "vessel"]}>
              <ModuleAccessCheck
                moduleName="VesselReport"
                allowedRoles={["fleet", "vessel"]}
              >
                <GeneralData />
              </ModuleAccessCheck>
            </PrivateRoute>
          }
        />

        <Route
          path="/vessel-dashboard"
          element={
            <PrivateRoute allowedRoles={["vessel", "fleet", "multiVessel"]}>
              <ModuleAccessCheck moduleName="VesselReport" allowedRoles={["vessel", "fleet", "multiVessel"]}>
                <VesselDashboard />
              </ModuleAccessCheck>
            </PrivateRoute>
          }
        />

        <Route
          path="/vessel/:vesselId"
          element={
            <PrivateRoute allowedRoles={["fleet", "user", "multiVessel"]}>
              <VesselDetails />
            </PrivateRoute>
          }
        />
 
        {/* Vessel Routes */}
        <Route
          path="/daily-reports"
          element={
            <PrivateRoute allowedRoles={["vessel"]}>
              <ModuleAccessCheck
                moduleName="DailyReports"
                allowedRoles={["vessel"]}
              >
                <DailyReport />
              </ModuleAccessCheck>
            </PrivateRoute>
          }
        />

        {/* Utility */}
        <Route path="/access-denied" element={<AccessDeniedWrapper />} />
 
        {/* Default Routes */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}
 
export default App;